// src/components/ui.jsx
// Shared, reusable UI components.
// No business logic — only rendering.

import { useState, useEffect, memo } from 'react';
import { T, S } from './theme';
import { timeAgo, formatTs }         from '@services';
import { StalenessTag }              from './StalenessTag';
import { ResourceFreshnessTag }      from './ResourceFreshnessTag';
import { SourceLabel }               from './SourceLabel';
import { ReporterBadge }            from './ReporterBadge';
import { VerificationBadge }        from './VerificationBadge';
import { SocialEvidenceButton }     from './SocialEvidencePanel';
import { SourceLineageCompact }     from './SourceLineage';
import { useVoiceAlerts }           from '@hooks/useVoiceAlerts';
import { getStaleness }              from '@services/staleness';

// ── BottomSheet ───────────────────────────────────────────────────
export function BottomSheet({ open, onClose, children, maxH = '80vh' }) {
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(open);

  useEffect(() => {
    if (open) { setMounted(true); requestAnimationFrame(() => setVisible(true)); }
    else       { setVisible(false); setTimeout(() => setMounted(false), 300); }
  }, [open]);

  if (!mounted) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000 }}>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute', inset: 0,
          background: `rgba(0,0,0,${visible ? 0.5 : 0})`,
          transition: 'background 0.3s',
        }}
      />
      {/* Sheet */}
      <div style={{
        position:     'absolute',
        bottom:       0,
        left:         0,
        right:        0,
        background:   T.card,
        borderRadius: '20px 20px 0 0',
        maxHeight:    maxH,
        overflowY:    'auto',
        WebkitOverflowScrolling: 'touch',
        transform:    `translateY(${visible ? '0' : '100%'})`,
        transition:   'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
        boxShadow:    '0 -4px 40px rgba(0,0,0,0.15)',
      }}>
        {/* Handle */}
        <div style={{ textAlign: 'center', padding: '10px 0 4px' }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: T.bg3, display: 'inline-block' }} />
        </div>
        {children}
      </div>
    </div>
  );
}

// ── FeedCard ──────────────────────────────────────────────────────
// ── Emerging detection visual config ────────────────────────────────
// Incidents auto-detected from clusters get a distinct amber treatment
// with an explicit "Needs Verification" label
function isEmergingDetection(post) {
  return post.source === 'emerging_detection' || post.needsVerification === true;
}

const POST_TYPES = {
  sos:   { emoji: '🚨', label: 'SOS',    color: '#dc2626' },
  alert: { emoji: '⚠️', label: 'ALERT',  color: '#d97706' },
  flood: { emoji: '🌊', label: 'FLOOD',  color: '#0369a1' },
  road:  { emoji: '🚗', label: 'ROAD',   color: '#b45309' },
  power: { emoji: '⚡', label: 'POWER',  color: '#7c3aed' },
  gas:   { emoji: '⛽', label: 'GAS',    color: '#059669' },
  good:  { emoji: '✅', label: 'UPDATE', color: '#059669' },
  info:  { emoji: 'ℹ️', label: 'INFO',   color: '#0369a1' },
};

// FeedCard is memoized to prevent re-renders from parent state changes.
// Cards only re-render when their specific post data changes.
// This matters most during confidence rescoring (every 5 min) — without
// memo, 30+ cards re-render even when their own data hasn't changed.
//
// Fields that trigger re-render (rendering-significant):
//   id, sev, body, type, ts, lat, address, confidenceMid, isHidden, isPinned
// Fields that do NOT trigger re-render:
//   parent filter state, sibling card updates, zone changes
function FeedCardComponent({ post, onLike }) {
  const { readCard, isSpeechAvailable } = useVoiceAlerts();
  const pt          = POST_TYPES[post.type] ?? POST_TYPES.info;
  const ageMin      = Math.floor((Date.now() - post.ts) / 60000);
  const decay       = ageMin < 5 ? 1 : ageMin < 60 ? 0.92 : ageMin < 180 ? 0.78 : 0.55;
  const isFresh     = ageMin < 3;
  // Intelligence engine marks isStale when confidence decays to near-zero
  const visuallyStale = post.isStale && !post.isPinned;
  // Map feed post type to staleness type (matches decay params)
  // Source label config
  const sourceConfig = {
    flood: 'community', road: 'community', power: 'community',
    gas: 'community', sos: 'community', shelter: 'gohsep',
  }[post.type];

  const stalenessType = {
    flood: 'flood', road: 'road', power: 'power',
    gas:   'gas',   sos:  'sos',  shelter: 'shelter',
    connectivity: 'connectivity',
    // Resource types — mapped for ResourceFreshnessTag
    gas_station:          'gas_station',
    shelter_availability: 'shelter_availability',
    medical_resource:     'medical_resource',
    charging_station:     'charging_station',
    supply_distribution:  'supply_distribution',
  }[post.type] ?? 'default';

  const RESOURCE_TYPES = new Set([
    'gas_station','shelter_availability','medical_resource',
    'charging_station','supply_distribution','gas','shelter',
  ]);
  const isResourceType = RESOURCE_TYPES.has(post.type);
  const sevColor = T.sev(post.sev);
  const sevShape = T.sevShape(post.sev);
  const [liked, setLiked] = useState(false);

  const handleLike = () => {
    setLiked(true);
    onLike?.(post.id);
  };

  return (
    <div style={{
      ...S.card,
      margin:     '0 14px 8px',
      borderLeft: `4px solid ${sevColor}`,
      opacity:    decay,
      position:   'relative',
      overflow:   'visible',
    }}>
      {/* Severity bar */}
      <div style={{ height: 2, background: sevColor, opacity: 0.5, borderRadius: '12px 12px 0 0' }} />
      {/* Colorblind shape indicator — only visible when .eos-colorblind is active */}
      <span
        className="eos-colorblind-shape"
        aria-hidden="true"
        style={{
          display:  'none',  /* hidden by default; .eos-colorblind CSS shows it */
          position: 'absolute', top: 4, right: 6,
          fontSize: 10, fontWeight: 900, color: sevColor,
        }}
      >
        {sevShape.shape} {sevShape.label}
      </span>

      {isFresh && (
        <div style={{
          position: 'absolute', top: -5, right: -5,
          background: T.red, color: '#fff',
          fontSize: 8, fontWeight: 900, padding: '2px 7px', borderRadius: 8,
          animation: 'blink 1s ease-in-out infinite', letterSpacing: 0.5,
        }}>LIVE</div>
      )}

      <div style={{ padding: '10px 12px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <span style={{ fontSize: 17 }}>{pt.emoji}</span>
          <span style={{
            fontSize: 9, fontWeight: 900, letterSpacing: 0.8,
            color: pt.color, background: pt.color + '15',
            padding: '2px 7px', borderRadius: 6,
          }}>{pt.label}</span>
          {/* Emerging detection — "Potential Incident · Needs Verification" label */}
          {isEmergingDetection(post) && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '2px 7px', marginBottom: 3,
              background: 'rgba(217,119,6,0.09)',
              border: '1px solid rgba(217,119,6,0.25)',
              borderRadius: 5, width: 'fit-content',
            }}>
              <span style={{ fontSize: 10 }}>🔍</span>
              <span style={{ fontSize: 9, fontWeight: 800, color: '#d97706',
                             textTransform: 'uppercase', letterSpacing: 0.8 }}>
                Potential Incident · Needs Verification
              </span>
            </div>
          )}
          {post.sev >= 4 && (
            <span style={{
              fontSize: 8, fontWeight: 900, color: sevColor,
              background: sevColor + '15', padding: '2px 6px', borderRadius: 5,
            }}>{post.sev >= 5 ? 'CRITICAL' : 'HIGH'}</span>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginLeft: 'auto' }}>
            {isResourceType
              ? <ResourceFreshnessTag type={stalenessType} tsMs={post.ts} confidence={post.confidenceMid} compact />
              : <StalenessTag type={stalenessType} tsMs={post.ts} compact />}
            {isSpeechAvailable && (
              <button
                onClick={(e) => { e.stopPropagation(); readCard(post); }}
                title="Read this report aloud"
                aria-label="Read this report aloud"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 13, padding: '2px 3px', opacity: 0.5,
                  lineHeight: 1,
                }}
              >
                🔊
              </button>
            )}
            <span style={{ fontSize: 10, color: T.dim, whiteSpace: 'nowrap' }}>
              {timeAgo(post.ts)}
            </span>
          </div>
        </div>

        {/* Body */}
        <div style={{ fontSize: 13, color: T.text, lineHeight: 1.55, marginBottom: 6 }}>
          {post.body}

          {/* Cluster stats for emerging detection */}
          {isEmergingDetection(post) && post.clusterStats && (
            <div style={{
              marginTop:   6, padding: '7px 9px',
              background:  'rgba(217,119,6,0.06)', borderRadius: 7,
              border:      '1px solid rgba(217,119,6,0.15)',
              display:     'flex', gap: 12, flexWrap: 'wrap',
            }}>
              {[
                { n: post.clusterStats.distinctReporters, label: 'reporters' },
                { n: post.clusterStats.mediaCount, label: 'photos' },
                { n: post.clusterStats.totalReports, label: 'reports' },
              ].filter(s => s.n > 0).map(s => (
                <div key={s.label} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 14, fontWeight: 900, color: '#d97706' }}>{s.n}</div>
                  <div style={{ fontSize: 8, color: '#92400e' }}>{s.label}</div>
                </div>
              ))}
              {post._nextStep && (
                <div style={{ fontSize: 9, color: '#92400e', flex: 1, lineHeight: 1.4, alignSelf: 'center' }}>
                  {post._nextStep}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Location */}
        {(post.lat || post.address) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6, fontSize: 10, color: T.blue, fontWeight: 700 }}>
            <span>📍</span>
            <span>{post.address ?? `${post.lat?.toFixed(4)}, ${post.lon?.toFixed(4)}`}</span>
          </div>
        )}

        {/* Media */}
        {post.media?.length > 0 && (
          <div style={{ display: 'flex', gap: 5, marginBottom: 6, flexWrap: 'wrap' }}>
            {post.media.map((m, i) => (
              <div key={i} style={{ width: 56, height: 56, borderRadius: 8, overflow: 'hidden', border: `1px solid ${T.bdr}`, background: T.bg2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {m.type?.startsWith('video')
                  ? <span style={{ fontSize: 22 }}>🎥</span>
                  : <img src={m.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                }
              </div>
            ))}
          </div>
        )}

        {/* Social evidence — shown when evidence exists */}
        <SocialEvidenceButton report={post} />

        {/* Verification score — shown when report has been scored */}
        {(post.verificationScore != null || post.isConflicting) && (
          <div style={{ paddingBottom: 6 }}>
            <VerificationBadge report={post} compact />
          </div>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 6, borderTop: `1px solid ${T.bdr}` }}>
          <div>
            <span style={{ fontSize: 11, fontWeight: 700, color: T.dim }}>
              <ReporterBadge
                tier={post.officialSource ? 'official' : (post.reporterTier ?? 'anonymous')}
                compact
              />
              {' '}{post.auth}
            </span>
            <div style={{ fontSize: 9, color: T.dim, marginTop: 1 }}>
              {formatTs(post.ts)} ({timeAgo(post.ts)})
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 10, fontWeight: 800, color: (post.likes ?? 0) >= 10 ? T.green : T.dim }}>
              {(post.likes ?? 0) >= 10 ? `✅ ${post.likes} verified` : `⚪ ${post.likes ?? 0}`}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm('Report this information as incorrect or misleading?')) {
                  onLike?.('flag', post.id);
                }
              }}
              title="Report incorrect info"
              aria-label="Report incorrect information"
              style={{
                background:'none', border:'none', cursor:'pointer',
                fontSize:12, color:T.dim, padding:'4px 6px',
                fontFamily:T.font, opacity:0.55, flexShrink:0,
              }}
            >
              ⚑
            </button>
            <button
              onClick={handleLike}
              disabled={liked}
              style={{ background: liked ? T.greenLight : 'none', border: `1px solid ${liked ? T.green : T.bdr}`, borderRadius: 8, padding: '3px 10px', fontSize: 11, color: liked ? T.green : T.dim, cursor: liked ? 'default' : 'pointer', fontFamily: T.font, fontWeight: 800 }}
            >
              {liked ? '✅' : 'Confirm'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── StatusChip ────────────────────────────────────────────────────

// Custom comparator — only re-render when rendering-significant fields change
export const FeedCard = memo(FeedCardComponent, (prev, next) => {
  // Return true = DO NOT re-render (props are equal enough)
  // Return false = DO re-render (something changed)
  return (
    prev.post.id            === next.post.id            &&
    prev.post.sev           === next.post.sev           &&
    prev.post.body          === next.post.body          &&
    prev.post.type          === next.post.type          &&
    prev.post.ts            === next.post.ts            &&
    prev.post.confidenceMid === next.post.confidenceMid &&
    prev.post.isHidden      === next.post.isHidden      &&
    prev.post.isPinned      === next.post.isPinned      &&
    prev.post.confirmCount  === next.post.confirmCount  &&
    prev.onLike             === next.onLike
  );
});

export function StatusChip({ online, queuedCount = 0, sourceStale = false, staleSourceName = null, backendState = null, sourceState = null }) {
  // Three states:
  // CONNECTED    — device online + sources fresh
  // SOURCE STALE — device online but a key source hasn't refreshed recently
  // OFFLINE      — device has no network
  const cfg = !online
    ? { bg: 'rgba(220,38,38,0.10)', border: T.red,   dot: T.red,   label: 'OFFLINE',   pulse: false }
    : (backendState === 'UNAVAILABLE' && sourceState === 'UNAVAILABLE')
    ? { bg: 'rgba(217,119,6,0.10)',  border: T.amber, dot: T.amber, label: 'BACKEND ⚠', pulse: false }
    : (backendState === 'DEGRADED' && sourceState === 'UNAVAILABLE')
    ? { bg: 'rgba(217,119,6,0.10)',  border: T.amber, dot: T.amber, label: 'DEGRADED',  pulse: true }
    : (sourceState === 'STALE' || sourceStale)
    ? { bg: 'rgba(217,119,6,0.10)',  border: T.amber, dot: T.amber, label: 'CONNECTED', pulse: true }
    : (sourceState === 'CACHED')
    ? { bg: 'rgba(217,119,6,0.10)',  border: T.amber, dot: T.amber, label: 'CACHED',    pulse: false }
    : { bg: 'rgba(5,150,105,0.10)', border: T.green, dot: T.green, label: 'CONNECTED', pulse: true };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '4px 10px', borderRadius: 20,
        background: cfg.bg, border: `1.5px solid ${cfg.border}30`,
      }}>
        <div style={{ width: 7, height: 7, borderRadius: '50%', background: cfg.dot,
          animation: cfg.pulse ? 'pulse 2s ease-in-out infinite' : 'none' }} />
        <span style={{ fontSize: 10, fontWeight: 900, color: cfg.border, letterSpacing: 0.8 }}>
          {cfg.label}
        </span>
        {queuedCount > 0 && (
          <span style={{ fontSize: 9, fontWeight: 800, color: T.amber }}>
            {queuedCount} queued
          </span>
        )}
      </div>
      {sourceStale && staleSourceName && (
        <div style={{ fontSize: 8, color: T.amber, fontWeight: 700, paddingRight: 4 }}>
          ⚠ {staleSourceName} stale
        </div>
      )}
    </div>
  );
}

// ── Toggle ────────────────────────────────────────────────────────
export function Toggle({ value, onChange, color = T.green }) {
  const { track, thumb } = S.toggle(value, color);
  return (
    <button onClick={() => onChange(!value)} style={track}>
      <div style={thumb} />
    </button>
  );
}

// ── SectionHeader ─────────────────────────────────────────────────
export function SectionHeader({ children }) {
  return <div style={S.sectionHeader}>{children}</div>;
}

// ── EmptyState ────────────────────────────────────────────────────
export function EmptyState({ icon, title, desc, action, onAction }) {
  return (
    <div style={{ textAlign: 'center', padding: '40px 24px' }}>
      <div style={{ fontSize: 52, marginBottom: 12 }}>{icon}</div>
      <div style={{ fontWeight: 900, fontSize: 18, color: T.text, marginBottom: 8 }}>{title}</div>
      <div style={{ fontSize: 13, color: T.dim, lineHeight: 1.6, marginBottom: action ? 20 : 0 }}>{desc}</div>
      {action && (
        <button onClick={onAction} style={{ ...S.btn(T.amber), maxWidth: 260, margin: '0 auto' }}>
          {action}
        </button>
      )}
    </div>
  );
}
